// src/admin/pages/users/userService.ts
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  serverTimestamp,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getAuth } from 'firebase/auth';

// UserRole is a plain string — role names are dynamic and user-defined.
// 'super-admin' is the only hardcoded special value (handled in AuthContext).
export type UserRole = string;

export interface User {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role?: UserRole;     // legacy field — kept for backward compat & super-admin check
  roleId?: string;     // id of the role document in 'roles' collection
  roleName?: string;   // denormalized display name of the role
  isActive?: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export interface PaginatedUsersResult {
  users: User[];
  totalCount: number;
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export interface UserStats {
  activeUsers: number;
  adminUsers: number;
}

const USER_CLAIMS_COLLECTION = 'userClaims';

export class UserService {
  // ── getUserStats ────────────────────────────────────────────────────────────
  // Permission check is handled at the UI/route level via hasPermission('users.view').
  // The service layer no longer enforces role-based guards — Firestore security
  // rules are the true enforcement layer.
  static async getUserStats(): Promise<UserStats> {
    try {
      const collectionRef = collection(db, USER_CLAIMS_COLLECTION);

      const activeQuery = query(collectionRef, where('isActive', '==', true));
      const activeSnapshot = await getCountFromServer(activeQuery);
      const activeUsers = activeSnapshot.data().count;

      // Count users whose legacy role field is 'admin' or whose roleName contains 'admin'
      const adminQuery = query(collectionRef, where('role', '==', 'super-admin'));
      const adminSnapshot = await getCountFromServer(adminQuery);
      const adminUsers = adminSnapshot.data().count;

      return { activeUsers, adminUsers };
    } catch (error: any) {
      console.error('Error fetching user stats:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access user statistics.');
      }
      throw error;
    }
  }

  // ── getUsersPaginated ───────────────────────────────────────────────────────
  static async getUsersPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    roleFilter?: string,   // roleId string from the roles collection
    statusFilter?: boolean
  ): Promise<PaginatedUsersResult> {
    try {
      const users: User[] = [];
      const collectionRef = collection(db, USER_CLAIMS_COLLECTION);

      const filterConstraints: any[] = [];

      if (roleFilter) {
        filterConstraints.push(where('roleId', '==', roleFilter));
      }

      if (statusFilter !== undefined) {
        filterConstraints.push(where('isActive', '==', statusFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(
            collectionRef,
            ...filterConstraints,
            orderConstraint,
            startAfter(cursorDoc),
            limit(pageSize)
          )
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const user: User = {
          uid: docSnap.id,
          email: data.email || '',
          displayName: data.displayName || null,
          photoURL: data.photoURL || null,
          role: data.role || 'user',
          roleId: data.roleId || null,
          roleName: data.roleName || null,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            user.email.toLowerCase().includes(searchLower) ||
            (user.displayName && user.displayName.toLowerCase().includes(searchLower)) ||
            (user.roleName && user.roleName.toLowerCase().includes(searchLower)) ||
            user.uid.toLowerCase().includes(searchLower);

          if (matchesSearch) users.push(user);
        } else {
          users.push(user);
        }
      });

      return {
        users,
        totalCount,
        lastVisible: null,
        firstVisible: null,
      };
    } catch (error: any) {
      console.error('Error fetching paginated users:', error);
      if (error.code === 'permission-denied') {
        throw new Error(
          'You do not have permission to access user data. Please contact an administrator.'
        );
      }
      throw error;
    }
  }

  // ── getAllUsers (deprecated) ─────────────────────────────────────────────────
  /** @deprecated Use getUsersPaginated instead */
  static async getAllUsers(): Promise<User[]> {
    try {
      const users: User[] = [];
      const claimsSnapshot = await getDocs(collection(db, USER_CLAIMS_COLLECTION));

      claimsSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        users.push({
          uid: docSnap.id,
          email: data.email || '',
          displayName: data.displayName || null,
          photoURL: data.photoURL || null,
          role: data.role || 'user',
          roleId: data.roleId || null,
          roleName: data.roleName || null,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        });
      });

      users.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      return users;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      if (error.code === 'permission-denied') {
        throw new Error(
          'You do not have permission to access user data. Please contact an administrator.'
        );
      }
      throw error;
    }
  }

  // ── getUserClaims ───────────────────────────────────────────────────────────
  static async getUserClaims(uid: string): Promise<User | null> {
    try {
      const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
      const userClaimsSnap = await getDoc(userClaimsRef);

      if (userClaimsSnap.exists()) {
        const data = userClaimsSnap.data();
        return {
          uid: userClaimsSnap.id,
          email: data.email || '',
          displayName: data.displayName || null,
          photoURL: data.photoURL || null,
          role: data.role || 'user',
          roleId: data.roleId || null,
          roleName: data.roleName || null,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching user claims:', error);
      if (error.code === 'permission-denied') {
        return null;
      }
      throw error;
    }
  }

  // ── createUserClaims ────────────────────────────────────────────────────────
  static async createUserClaims(
    uid: string,
    email: string,
    displayName?: string,
    photoURL?: string
  ): Promise<void> {
    try {
      const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
      const existingClaims = await getDoc(userClaimsRef);

      if (!existingClaims.exists()) {
        await setDoc(userClaimsRef, {
          email,
          displayName: displayName || null,
          photoURL: photoURL || null,
          role: 'user',
          roleId: null,
          roleName: null,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(userClaimsRef, {
          email,
          displayName: displayName || null,
          photoURL: photoURL || null,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error: any) {
      console.error('Error creating user claims:', error);
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Unable to create user claims.');
      }
      throw error;
    }
  }

  // ── updateUserRole ──────────────────────────────────────────────────────────
  // Permission check (users.edit / users.assign_role) is done at UI level.
  static async updateUserRole(
    uid: string,
    roleId: string,
    roleName: string,
    legacyRole: UserRole = 'user'
  ): Promise<void> {
    try {
      const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
      await updateDoc(userClaimsRef, {
        roleId,
        roleName,
        role: legacyRole,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error updating user role:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update user roles');
      }
      throw error;
    }
  }

  // ── toggleUserStatus ────────────────────────────────────────────────────────
  // Permission check (users.toggle_status) is done at UI level.
  static async toggleUserStatus(uid: string, isActive: boolean): Promise<void> {
    try {
      const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
      await updateDoc(userClaimsRef, {
        isActive,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update user status');
      }
      throw error;
    }
  }

  // ── deleteUser ──────────────────────────────────────────────────────────────
  // Permission check (users.delete) is done at UI level.
  static async deleteUser(uid: string): Promise<void> {
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) throw new Error('You must be logged in');

      if (currentUser.uid === uid) {
        throw new Error('You cannot delete your own account');
      }

      const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
      await deleteDoc(userClaimsRef);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to delete users');
      }
      throw error;
    }
  }

  // ── hasRole ─────────────────────────────────────────────────────────────────
  static async hasRole(uid: string, role: UserRole): Promise<boolean> {
    try {
      const userClaims = await this.getUserClaims(uid);
      return userClaims?.role === role;
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  }

  // ── isUserActive ─────────────────────────────────────────────────────────────
  static async isUserActive(uid: string): Promise<boolean> {
    try {
      const userClaims = await this.getUserClaims(uid);
      return userClaims?.isActive !== false;
    } catch (error) {
      console.error('Error checking user active status:', error);
      return false;
    }
  }
}


// import {
//   collection,
//   doc,
//   setDoc,
//   updateDoc,
//   deleteDoc,
//   getDocs,
//   getDoc,
//   query,
//   serverTimestamp,
//   where,
//   orderBy,
//   limit,
//   startAfter,
//   getCountFromServer,
//   QueryDocumentSnapshot,
//   DocumentData,
//   Query,
// } from 'firebase/firestore';
// import { db } from '../../../firebase/config';
// import { getAuth } from 'firebase/auth';

// // Keep 'admin' | 'manager' for internal permission checks only.
// // All other role assignment now uses dynamic roleId from the roles collection.
// export type UserRole = string;

// export interface User {
//   uid: string;
//   email: string;
//   displayName?: string;
//   photoURL?: string;
//   role?: UserRole;         // legacy field — kept for backward compat & permission checks
//   roleId?: string;         // NEW: id of the role document in 'roles' collection
//   roleName?: string;       // NEW: denormalized display name of the role
//   isActive?: boolean;
//   createdAt?: any;
//   updatedAt?: any;
// }

// export interface PaginatedUsersResult {
//   users: User[];
//   totalCount: number;
//   lastVisible: QueryDocumentSnapshot<DocumentData> | null;
//   firstVisible: QueryDocumentSnapshot<DocumentData> | null;
// }

// export interface UserStats {
//   activeUsers: number;
//   adminUsers: number;
// }

// const USER_CLAIMS_COLLECTION = 'userClaims';

// export class UserService {
//   static async getUserStats(): Promise<UserStats> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) {
//         throw new Error('You must be logged in to view user stats');
//       }

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin' && currentUserClaims?.role !== 'manager') {
//         throw new Error('You do not have permission to view user stats');
//       }

//       const collectionRef = collection(db, USER_CLAIMS_COLLECTION);

//       const activeQuery = query(collectionRef, where('isActive', '==', true));
//       const activeSnapshot = await getCountFromServer(activeQuery);
//       const activeUsers = activeSnapshot.data().count;

//       const adminQuery = query(collectionRef, where('role', '==', 'admin'));
//       const adminSnapshot = await getCountFromServer(adminQuery);
//       const adminUsers = adminSnapshot.data().count;

//       return { activeUsers, adminUsers };
//     } catch (error: any) {
//       console.error('Error fetching user stats:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to access user statistics.');
//       }
//       throw error;
//     }
//   }

//   static async getUsersPaginated(
//     pageSize: number = 10,
//     page: number = 1,
//     searchQuery?: string,
//     roleFilter?: string,       // now accepts any roleId string (or legacy role value)
//     statusFilter?: boolean
//   ): Promise<PaginatedUsersResult> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) {
//         throw new Error('You must be logged in to view users');
//       }

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin' && currentUserClaims?.role !== 'manager') {
//         throw new Error('You do not have permission to view users');
//       }

//       const users: User[] = [];
//       const collectionRef = collection(db, USER_CLAIMS_COLLECTION);

//       const filterConstraints: any[] = [];

//       if (roleFilter) {
//         // Filter by roleId (new) — falls back to legacy `role` field if no roleId stored
//         filterConstraints.push(where('roleId', '==', roleFilter));
//       }

//       if (statusFilter !== undefined) {
//         filterConstraints.push(where('isActive', '==', statusFilter));
//       }

//       const orderConstraint = orderBy('createdAt', 'desc');

//       const countSnapshot = await getCountFromServer(
//         query(collectionRef, ...filterConstraints)
//       );
//       const totalCount = countSnapshot.data().count;

//       let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
//       const skipCount = (page - 1) * pageSize;

//       if (skipCount > 0) {
//         const seekSnapshot = await getDocs(
//           query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
//         );
//         if (!seekSnapshot.empty) {
//           cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
//         }
//       }

//       const pageQuery: Query<DocumentData> = cursorDoc
//         ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
//         : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

//       const snapshot = await getDocs(pageQuery);

//       snapshot.docs.forEach(docSnap => {
//         const data = docSnap.data();
//         const user: User = {
//           uid: docSnap.id,
//           email: data.email || '',
//           displayName: data.displayName || null,
//           photoURL: data.photoURL || null,
//           role: data.role || 'user',
//           roleId: data.roleId || null,
//           roleName: data.roleName || null,
//           isActive: data.isActive !== false,
//           createdAt: data.createdAt?.toDate(),
//           updatedAt: data.updatedAt?.toDate(),
//         };

//         if (searchQuery && searchQuery.trim() !== '') {
//           const searchLower = searchQuery.toLowerCase();
//           const matchesSearch =
//             user.email.toLowerCase().includes(searchLower) ||
//             (user.displayName && user.displayName.toLowerCase().includes(searchLower)) ||
//             (user.roleName && user.roleName.toLowerCase().includes(searchLower)) ||
//             user.uid.toLowerCase().includes(searchLower);

//           if (matchesSearch) users.push(user);
//         } else {
//           users.push(user);
//         }
//       });

//       return {
//         users,
//         totalCount,
//         lastVisible: null,
//         firstVisible: null,
//       };
//     } catch (error: any) {
//       console.error('Error fetching paginated users:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to access user data. Please contact an administrator.');
//       }
//       throw error;
//     }
//   }

//   /** @deprecated Use getUsersPaginated instead */
//   static async getAllUsers(): Promise<User[]> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) {
//         throw new Error('You must be logged in to view users');
//       }

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin' && currentUserClaims?.role !== 'manager') {
//         throw new Error('You do not have permission to view users');
//       }

//       const users: User[] = [];
//       const claimsSnapshot = await getDocs(collection(db, USER_CLAIMS_COLLECTION));

//       claimsSnapshot.docs.forEach(doc => {
//         const data = doc.data();
//         users.push({
//           uid: doc.id,
//           email: data.email || '',
//           displayName: data.displayName || null,
//           photoURL: data.photoURL || null,
//           role: data.role || 'user',
//           roleId: data.roleId || null,
//           roleName: data.roleName || null,
//           isActive: data.isActive !== false,
//           createdAt: data.createdAt?.toDate(),
//           updatedAt: data.updatedAt?.toDate(),
//         });
//       });

//       users.sort((a, b) => {
//         if (!a.createdAt) return 1;
//         if (!b.createdAt) return -1;
//         return b.createdAt.getTime() - a.createdAt.getTime();
//       });

//       return users;
//     } catch (error: any) {
//       console.error('Error fetching users:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to access user data. Please contact an administrator.');
//       }
//       throw error;
//     }
//   }

//   static async getUserClaims(uid: string): Promise<User | null> {
//     try {
//       const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
//       const userClaimsSnap = await getDoc(userClaimsRef);

//       if (userClaimsSnap.exists()) {
//         const data = userClaimsSnap.data();
//         return {
//           uid: userClaimsSnap.id,
//           email: data.email || '',
//           displayName: data.displayName || null,
//           photoURL: data.photoURL || null,
//           role: data.role || 'user',
//           roleId: data.roleId || null,
//           roleName: data.roleName || null,
//           isActive: data.isActive !== false,
//           createdAt: data.createdAt?.toDate(),
//           updatedAt: data.updatedAt?.toDate(),
//         };
//       }

//       return null;
//     } catch (error: any) {
//       console.error('Error fetching user claims:', error);
//       if (error.code === 'permission-denied') {
//         return null;
//       }
//       throw error;
//     }
//   }

//   static async createUserClaims(
//     uid: string,
//     email: string,
//     displayName?: string,
//     photoURL?: string
//   ): Promise<void> {
//     try {
//       const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
//       const existingClaims = await getDoc(userClaimsRef);

//       if (!existingClaims.exists()) {
//         await setDoc(userClaimsRef, {
//           email,
//           displayName: displayName || null,
//           photoURL: photoURL || null,
//           role: 'user',
//           roleId: null,
//           roleName: null,
//           isActive: true,
//           createdAt: serverTimestamp(),
//           updatedAt: serverTimestamp(),
//         });
//       } else {
//         await updateDoc(userClaimsRef, {
//           email,
//           displayName: displayName || null,
//           photoURL: photoURL || null,
//           updatedAt: serverTimestamp(),
//         });
//       }
//     } catch (error: any) {
//       console.error('Error creating user claims:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('Permission denied. Unable to create user claims.');
//       }
//       throw error;
//     }
//   }

//   /**
//    * Update a user's assigned role using a dynamic role from the roles collection.
//    * Also keeps the legacy `role` field in sync for permission checks.
//    */
//   static async updateUserRole(
//     uid: string,
//     roleId: string,
//     roleName: string,
//     legacyRole: UserRole = 'user'
//   ): Promise<void> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) throw new Error('You must be logged in');

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin') {
//         throw new Error('Only administrators can update user roles');
//       }

//       const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
//       await updateDoc(userClaimsRef, {
//         roleId,
//         roleName,
//         role: legacyRole,   // keep legacy field for permission checks
//         updatedAt: serverTimestamp(),
//       });
//     } catch (error: any) {
//       console.error('Error updating user role:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to update user roles');
//       }
//       throw error;
//     }
//   }

//   static async toggleUserStatus(uid: string, isActive: boolean): Promise<void> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) throw new Error('You must be logged in');

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin') {
//         throw new Error('Only administrators can update user status');
//       }

//       const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
//       await updateDoc(userClaimsRef, {
//         isActive,
//         updatedAt: serverTimestamp(),
//       });
//     } catch (error: any) {
//       console.error('Error toggling user status:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to update user status');
//       }
//       throw error;
//     }
//   }

//   static async deleteUser(uid: string): Promise<void> {
//     try {
//       const auth = getAuth();
//       const currentUser = auth.currentUser;

//       if (!currentUser) throw new Error('You must be logged in');

//       if (currentUser.uid === uid) {
//         throw new Error('You cannot delete your own account');
//       }

//       const currentUserClaims = await this.getUserClaims(currentUser.uid);
//       if (currentUserClaims?.role !== 'admin') {
//         throw new Error('Only administrators can delete users');
//       }

//       const userClaimsRef = doc(db, USER_CLAIMS_COLLECTION, uid);
//       await deleteDoc(userClaimsRef);
//     } catch (error: any) {
//       console.error('Error deleting user:', error);
//       if (error.code === 'permission-denied') {
//         throw new Error('You do not have permission to delete users');
//       }
//       throw error;
//     }
//   }

//   static async hasRole(uid: string, role: UserRole): Promise<boolean> {
//     try {
//       const userClaims = await this.getUserClaims(uid);
//       return userClaims?.role === role;
//     } catch (error) {
//       console.error('Error checking user role:', error);
//       return false;
//     }
//   }

//   static async hasMinimumRole(uid: string, minimumRole: UserRole): Promise<boolean> {
//     try {
//       const userClaims = await this.getUserClaims(uid);
//       if (!userClaims) return false;

//       const roleHierarchy: Record<UserRole, number> = {
//         user: 1,
//         staff: 2,
//         manager: 3,
//         admin: 4,
//       };

//       const userRoleLevel = roleHierarchy[userClaims.role || 'user'];
//       const minimumRoleLevel = roleHierarchy[minimumRole];

//       return userRoleLevel >= minimumRoleLevel;
//     } catch (error) {
//       console.error('Error checking minimum role:', error);
//       return false;
//     }
//   }

//   static async isUserActive(uid: string): Promise<boolean> {
//     try {
//       const userClaims = await this.getUserClaims(uid);
//       return userClaims?.isActive !== false;
//     } catch (error) {
//       console.error('Error checking user active status:', error);
//       return false;
//     }
//   }
// }
